import { useState, useEffect } from "react";

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(window.deferredPrompt || null);
  const [isInstallable, setIsInstallable] = useState(!!window.deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // 1. Check if already running in standalone mode
    const isStandalone = 
      window.matchMedia("(display-mode: standalone)").matches || 
      window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // 2. Listen for the browser's beforeinstallprompt event
    const handleBeforePrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // 3. Listen for our custom early-captured prompt event
    const handleCustomPromptReady = (e) => {
      const promptEvent = e.detail;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
    };

    // 4. Listen for successful installation event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    };

    window.addEventListener("beforeinstallprompt", handleBeforePrompt);
    window.addEventListener("pwa-prompt-ready", handleCustomPromptReady);
    window.addEventListener("appinstalled", handleAppInstalled);

    // If deferredPrompt is already on window, update state immediately
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforePrompt);
      window.removeEventListener("pwa-prompt-ready", handleCustomPromptReady);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    const activePrompt = deferredPrompt || window.deferredPrompt;
    if (!activePrompt) {
      return false;
    }
    try {
      activePrompt.prompt();
      const { outcome } = await activePrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstallable(false);
        setDeferredPrompt(null);
        window.deferredPrompt = null;
        return true;
      }
    } catch (err) {
      console.error("PWA Prompt error during installApp:", err);
    }
    return false;
  };

  return { isInstallable, isInstalled, installApp };
}
